import React from 'react'
import { useTranslation } from 'react-i18next'
import { Fixed } from '../../parts/bases/Fixed'
import { HStack } from '../../parts/bases/HStack'
import { Text } from '../../parts/bases/Text'
import { VStack } from '../../parts/bases/VStack'
import { PinkNeonButton } from '../../parts/buttons/PinkNeonButton'
import Image, { StaticImageData } from 'next/image'
import { Center } from '../../parts/bases/Center'
import { Spinner } from '../../parts/commons/Spinner'
import config from '@/tailwind.config'
import Teras from '@/public/images/teras.png'
import AmInstallArrow from '@/public/images/amInstallArrow.png'
import CheckIcon from '@/public/images/check.png'
import CheckActiveIcon from '@/public/images/check_active.png'
import CheckDisabled from '@/public/images/check_disabled.png'
import Akv from '@/public/images/akv.png'
import AkvDisabled from '@/public/images/akvDisabled.png'
import { CraftCurrencyType } from './query.generated'
import { VideoPlayer } from '../../parts/commons/VideoPlayer'

type PartsType = 'AP' | 'Junk'

export type Props = {
  status: 'Prepare' | 'Loading' | 'Completed'
  parts: {
    rom?: { id: string; name: string; image: string; type: PartsType; junksPerPart?: number }
    accumulator?: { id: string; name: string; image: string; type: PartsType; junksPerPart?: number }
    upperCabinet?: { id: string; name: string; image: string; type: PartsType; junksPerPart?: number }
    lowerCabinet?: { id: string; name: string; image: string; type: PartsType; junksPerPart?: number }
  }
  onClickCraft: () => void
  onClickClose: () => void
  onCompleted: () => void
  onClickInstall: () => void
  createdAm?: { name: string; animationUrl: string }
  akvBalance: number
  akvFee: number
  terasBalance: number
  terasFee: number
  usedCurrency: CraftCurrencyType
  setUsedCurrency: (type: CraftCurrencyType) => void
}

export const Layout = (props: Props) => {
  const { t } = useTranslation()
  return (
    <Fixed className="top-0 left-0 right-0 bottom-0 z-[100] h-full w-full items-center justify-center bg-dialogBg">
      <div className="flex h-full w-full items-center justify-center" onClick={props.onClickClose}>
        <VStack
          className="items-center justify-center rounded-xl border-2 border-neon-blue bg-black p-2"
          onClick={(event) => event.stopPropagation()}
        >
          {props.status === 'Prepare' ? (
            <VStack className="w-full items-center">
              <Text className="mt-4 text-2xl font-bold text-white">{t('AMCraftDialog.title')}</Text>
              <HStack className="my-10 mx-4 w-full items-start justify-center">
                <VStack className="mx-3 items-center">
                  <Text className="mb-2 flex-1 text-sm font-bold text-white">
                    {t('AMCraftDialog.romLabel')}
                  </Text>
                  <Center className="relative aspect-square w-[140px]  overflow-hidden rounded-lg border border-[#805BE7]">
                    <Image src={props.parts.rom?.image ?? ''} layout="fill" objectFit="cover" alt="" />
                  </Center>
                  <Text className="mt-2 w-[120px] flex-1 justify-center text-center text-xs font-bold text-white">
                    {props.parts.rom?.name}
                  </Text>
                  {props.parts.rom?.type === 'Junk' ? (
                    <JunkLabel name={props.parts.rom.name} junksPerPart={props.parts.rom.junksPerPart ?? 0} />
                  ) : null}
                </VStack>
                <VStack className="mx-3 items-center">
                  <Text className="mb-2 flex-1 text-sm font-bold text-white">
                    {t('AMCraftDialog.accumulatorLabel')}
                  </Text>
                  <Center className="relative aspect-square w-[140px] overflow-hidden rounded-lg border border-[#805BE7]">
                    <Image
                      src={props.parts.accumulator?.image ?? ''}
                      layout="fill"
                      objectFit="cover"
                      alt=""
                    />
                  </Center>
                  <Text className="mt-2 w-[120px] flex-1 justify-center text-center text-xs font-bold text-white">
                    {props.parts.accumulator?.name}
                  </Text>
                  {props.parts.accumulator?.type === 'Junk' ? (
                    <JunkLabel
                      name={props.parts.accumulator.name}
                      junksPerPart={props.parts.accumulator.junksPerPart ?? 0}
                    />
                  ) : null}
                </VStack>
                <VStack className="mx-3 items-center">
                  <Text className="mb-2 flex-1 text-sm font-bold text-white">
                    {t('AMCraftDialog.upperCabinetLabel')}
                  </Text>
                  <Center className="relative aspect-square w-[140px]  overflow-hidden rounded-lg border border-[#805BE7]">
                    <Image
                      src={props.parts.upperCabinet?.image ?? ''}
                      layout="fill"
                      objectFit="cover"
                      alt=""
                    />
                  </Center>
                  <Text className="mt-2 w-[120px] flex-1 justify-center text-center text-xs font-bold text-white">
                    {props.parts.upperCabinet?.name}
                  </Text>
                  {props.parts.upperCabinet?.type === 'Junk' ? (
                    <JunkLabel
                      name={props.parts.upperCabinet.name}
                      junksPerPart={props.parts.upperCabinet.junksPerPart ?? 0}
                    />
                  ) : null}
                </VStack>
                <VStack className="mx-3 items-center">
                  <Text className="mb-2 flex-1 text-sm font-bold text-white">
                    {t('AMCraftDialog.lowerCabinetLabel')}
                  </Text>
                  <Center className="relative aspect-square w-[140px]  overflow-hidden rounded-lg border border-[#805BE7]">
                    <Image
                      src={props.parts.lowerCabinet?.image ?? ''}
                      layout="fill"
                      objectFit="cover"
                      alt=""
                    />
                  </Center>
                  <Text className="mt-2 w-[120px] flex-1 justify-center text-center text-xs font-bold text-white">
                    {props.parts.lowerCabinet?.name}
                  </Text>
                  {props.parts.lowerCabinet?.type === 'Junk' ? (
                    <JunkLabel
                      name={props.parts.lowerCabinet.name}
                      junksPerPart={props.parts.lowerCabinet.junksPerPart ?? 0}
                    />
                  ) : null}
                </VStack>
              </HStack>
              <HStack className="mb-8 gap-x-10">
                <VStack>
                  <Text className="text-sm text-white">{t('AMCraftDialog.selectCurrency')}</Text>
                  <HStack className="gap-x-7">
                    <CheckButton
                      image={props.akvBalance === 0 ? AkvDisabled : Akv}
                      label="AKV"
                      fee={props.akvFee}
                      checked={CraftCurrencyType.Akv === props.usedCurrency}
                      disabled={props.akvBalance === 0}
                      onClick={() => props.setUsedCurrency(CraftCurrencyType.Akv)}
                    />
                    <CheckButton
                      image={Teras}
                      label="Teras"
                      fee={props.terasFee}
                      checked={CraftCurrencyType.Teras === props.usedCurrency}
                      onClick={() => props.setUsedCurrency(CraftCurrencyType.Teras)}
                    />
                  </HStack>
                </VStack>
                <HStack className="w-[300px] items-center rounded-sm bg-[#00FBEC] bg-opacity-10 py-2 px-4">
                  <Center className="mr-5 h-[18px] w-[18px]">
                    <Image
                      src={props.usedCurrency === CraftCurrencyType.Teras ? Teras : Akv}
                      width={18}
                      height={18}
                      alt=""
                    />
                  </Center>
                  <Text className="flex-1 text-center text-base font-bold text-neon-blue">
                    {Number(
                      props.usedCurrency === CraftCurrencyType.Teras ? props.terasBalance : props.akvBalance,
                    ).toLocaleString()}
                  </Text>
                  <Center className="h-[8px] w-[10px]">
                    <Image src={AmInstallArrow} width={8} height={10} alt="" />
                  </Center>
                  <Text className="flex-1 text-center text-base font-bold text-[#1573FF]">
                    {(props.usedCurrency === CraftCurrencyType.Teras
                      ? props.terasBalance - props.terasFee
                      : props.akvBalance - props.akvFee
                    ).toLocaleString()}
                  </Text>
                </HStack>
              </HStack>
              <Text className="mx-8 mb-8 text-center text-sm font-semibold text-white">
                {t('AMCraftDialog.description1')}
                {'\n'}
                {t('AMCraftDialog.description2')}
              </Text>
              <HStack className="mb-8">
                <PinkNeonButton onClick={props.onClickClose} type="Sub">
                  {t('AMCraftDialog.cancelButton')}
                </PinkNeonButton>
                <VStack className="w-4" />
                <PinkNeonButton
                  onClick={props.onClickCraft}
                  disabled={
                    props.usedCurrency === CraftCurrencyType.Teras
                      ? props.terasBalance < props.terasFee
                      : props.akvBalance < props.akvFee
                  }
                >
                  {t('AMCraftDialog.craftButton')}
                </PinkNeonButton>
              </HStack>
            </VStack>
          ) : null}
          {props.status === 'Loading' ? (
            <Center className="h-[50px] w-[400px]">
              <Spinner size={30} visible color={config.theme.extend.colors.neon.blue} />
            </Center>
          ) : null}
          {props.status === 'Completed' ? (
            <VStack className="w-[500px] items-center">
              <Text className="mt-4 text-2xl font-bold text-white">{t('AMCraftDialog.title')}</Text>
              <Text className="mt-4 text-sm font-bold text-neon-blue">
                {t('AMCraftDialog.completedMessage', { amName: props.createdAm?.name ?? '---' })}
              </Text>
              <Center className="mt-2 h-[300px] w-[300px] overflow-hidden rounded-lg border border-[#805BE7]">
                {props.createdAm?.animationUrl && (
                  <VideoPlayer src={props.createdAm?.animationUrl} loop={false} />
                )}
              </Center>
              <Text className="mt-4 text-sm font-bold text-white">
                {t('AMCraftDialog.completedDescription')}
              </Text>
              <HStack>
                <Center className="mt-4 mb-4">
                  <PinkNeonButton type="Sub" onClick={props.onCompleted}>
                    Close
                  </PinkNeonButton>
                </Center>
                <VStack className="w-8" />
                <Center className="mt-4 mb-4">
                  <PinkNeonButton onClick={props.onClickInstall}>Install</PinkNeonButton>
                </Center>
              </HStack>
            </VStack>
          ) : null}
        </VStack>
      </div>
    </Fixed>
  )
}

const JunkLabel = (props: { name: string; junksPerPart: number }) => {
  return (
    <Center className="mt-1 w-28 flex-col">
      <Text className="text-center text-xs font-bold text-akiverse-pink">
        Junk {props.name} x {props.junksPerPart}
      </Text>
    </Center>
  )
}

const CheckButton = (proeps: {
  image: StaticImageData
  label: string
  fee: number
  checked: boolean
  disabled?: boolean
  onClick: () => void
}) => {
  const { image, label, fee, checked, disabled, onClick } = proeps
  const textColor = disabled ? 'text-white/40' : 'text-white'
  return (
    <HStack
      className={`items-center ${disabled ? '' : 'cursor-pointer'}`}
      onClick={(event) => {
        if (disabled) return
        event.preventDefault()
        onClick()
      }}
    >
      <button className="-mt-[2px] h-4 w-4" disabled={disabled}>
        <Image src={disabled ? CheckDisabled : checked ? CheckActiveIcon : CheckIcon} alt="check" />
      </button>
      <Center className="ml-2 h-4 w-4">
        <Image src={image} alt={label} />
      </Center>
      <Text className={`ml-2 text-base ${textColor}`}>{label}</Text>
      <Text className={`ml-3 text-base ${textColor}`}>{Number(fee).toLocaleString()}</Text>
    </HStack>
  )
}
